import { createOrder, validateOrder } from '../../../api/orders'
import { trackPaymentFailure } from '../../../utils/trackFunnel';

export const fetchPaymentIntent = async ({ stripe, setCallbackErrors, cartKey, setIsSubmitting, setCustomer, history,
                                           setShowBilling, setValue, customer, clientSecret
}) => {
  const { error, paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

  if (error) {
    trackPaymentFailure('afterpay', { cartId: customer[cartKey]?.id, stage: 'retrieve_payment_intent', error });
    setCallbackErrors([error.message || 'There was an error processing your payment. Please try again or use another payment method.'])
  }

  if (paymentIntent.status !== 'succeeded') {
    const clearPayError = paymentIntent.last_payment_error && paymentIntent.last_payment_error.message
    trackPaymentFailure('afterpay', {
      cartId: customer[cartKey]?.id,
      stage: 'payment_intent_status',
      error: paymentIntent.last_payment_error,
      properties: { status: paymentIntent.status },
    });
    setCallbackErrors([clearPayError || 'There was an error processing your payment. Please try again or use another payment method.'])
    setShowBilling(true);
    setValue("payment_method", "clearpay");

    return;
  }

  let fullName = paymentIntent.shipping.name.split(' ');
  let firstName = fullName.shift();
  let lastName = fullName.join(' ');

  let formattedData = {
    first_name: firstName,
    last_name: lastName,
    phone: paymentIntent.shipping.phone,
    shipping_address: paymentIntent.shipping.address.line1,
    shipping_address2: paymentIntent.shipping.address.line2,
    shipping_city: paymentIntent.shipping.address.city,
    shipping_state: paymentIntent.shipping.address.state,
    shipping_zip: paymentIntent.shipping.address.postal_code,
    cart_id: customer[cartKey].id,
    email: customer[cartKey].email,
    paypal_order: JSON.stringify(paymentIntent),
    paid_with_paypal: true,
  };

  createOrder({ order: formattedData })
    .then((response) => {
      history.push(`/order/complete/${response.data.order.id}/${response.data.order.email}`)
      customer[cartKey] = null;
    })
    .catch((error) => {
      setCallbackErrors((error.response && error.response.data && error.response.data.data) || ['Something went wrong. Please try again. You have not been charged.']);
    })
    .then(() => {
      setIsSubmitting(false);
    });
}

export const handleClearpay = async({ data, cart, setOrderErrors, setIsSubmitting, stripe, watchSameAsBilling, customer }) => {
  const { client_secret, error } = await fetch(
    `/api/v1/carts/${cart.id}/create_payment_intent`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentMethodType: 'afterpay_clearpay',
        currency: 'usd',
      }),
    }
  ).then((response) => response.json())

  if (error) {
    trackPaymentFailure('afterpay', { cartId: cart?.id, stage: 'create_payment_intent', error });
    setOrderErrors(['There was an error processing your payment. Please try again or use another payment method.'])
    setIsSubmitting(false);
    return;
  }

  const { error: stripeError } = await stripe.confirmAfterpayClearpayPayment(client_secret, {
    payment_method: {
      billing_details: {
        name: `${data.first_name} ${data.last_name}`,
        email: customer.email,
        address: {
          line1: data.address,
          line2: data.address2,
          city: data.city,
          state: data.state && data.state.value,
          country: 'US',
          postal_code: data.zip_code,
        },
      },
    },
    shipping: {
      name: `${data.first_name} ${data.last_name}`,
      phone: data.phone,
      address: {
        line1: watchSameAsBilling ? data.address : data.shipping_address,
        line2: watchSameAsBilling ? data.address2 : data.shipping_address2,
        city: watchSameAsBilling ? data.city : data.shipping_city,
        state: watchSameAsBilling ? (data.state && data.state.value) : data.shipping_state && data.shipping_state.value,
        country: 'US',
        postal_code: watchSameAsBilling ? data.zip_code : data.shipping_zip
      }
    },
    return_url: `${window.location.origin}/c/checkout?return=true`
  })

  if (stripeError) {
    trackPaymentFailure('afterpay', { cartId: cart?.id, stage: 'confirm_payment', error: stripeError });
    setOrderErrors([stripeError.message || 'There was an error processing your payment. Please try again or use another payment method.'])
    setIsSubmitting(false);
  }
}

export const onPayPalApprove = ({ data, actions, setValue, setShowShipping, usStates }) => {
  setValue("payment_method", "paypal");
  setShowShipping(true);

  actions.order.get().then(orderDetails => {
    const state = usStates.find(s => s.value === orderDetails.purchase_units[0].shipping.address.admin_area_1);
    setValue('shipping_address', orderDetails.purchase_units[0].shipping.address.address_line_1)
    setValue('shipping_address2', orderDetails.purchase_units[0].shipping.address.address_line_2)
    setValue('shipping_state', state)
    setValue('shipping_city', orderDetails.purchase_units[0].shipping.address.admin_area_2)
    setValue('shipping_zip', orderDetails.purchase_units[0].shipping.address.postal_code)
  })

  window.paypalOrder = actions.order;
};

// Validators
export const validateCustomerInfo = async ({ trigger, setCustomer, customer, watch, setShowBilling, errors }) => {
  await trigger(['first_name', 'last_name', 'phone'])
  if (Object.keys(errors).length !== 0) return;

  setCustomer({ ...customer, firstName: watch('first_name'), lastName: watch('last_name'), phone: watch('phone') })
  setShowBilling(true)
}

export const validatePaymentMethod = async ({ trigger, errors, setShowShipping }) => {
  await trigger(["first_name", "last_name", "phone", "payment_method", "name_on_card", "card_number",
    "card_expires_on", "card_verification", "address", "address2", "city", "state", "zip_code"])

  if (Object.keys(errors).length !== 0) return;

  setShowShipping(true)
}

export const assignOrderData = ({ data, customer, cartKey, payPalOrderId, paidWithPayPal }) => (
  { ...data,
    shipping_state: data.shipping_state && data.shipping_state.value,
    state: data.state && data.state.value,
    cart_id: customer[cartKey].id,
    card_number: data.card_number && data.card_number.replace(/ /g, ''),
    paypal_order: payPalOrderId,
    paid_with_paypal: paidWithPayPal,
  }
)

export const validateNewOrder = async ({ initialOrderData }) => {
  try {
    const response = await validateOrder({ order: initialOrderData });
    return { orderIsValid: response.data.valid, orderErrors: response.data.errors }
  } catch (err) {
    return { orderIsValid: false }
  }
}
